import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomDayCheckbox from './CustomDayCheckbox';
import {DAYS_MODEL} from "../Apis/functions";
import {FormHelperText} from "@mui/material";
import {CustomStyleFormHelperText} from "./Styles";
import {colors} from '../enums/colors';
import {Box} from "@mui/material";
/**
 * @Author: Susana Garcia Rodriguez
 * @JobTitle: Frontend - IBL
 * @Email: sgarcia@ibl.mx
 *
 * CustomDaySelector is a reusable component for selecting multiple days of the week.
 * It renders a row of checkboxes using `CustomDayCheckbox` and supports readonly mode,
 * custom colors, and validation for required fields.
 *
 * @component
 *
 * @param {string} [id="cb_cds_day_selector"] - Base id for the checkboxes.
 * @param {Array<number>} dayArray - Array of selected day values (required).
 * @param {Function} setDayArray - Function to update the selected days (required).
 * @param {string} [containerClasses=""] - Extra CSS classes for the container.
 * @param {boolean} [readonly=false] - If true, disables interactions and applies readonly styles.
 * @param {boolean} [required=false] - Marks the field as required for validation.
 * @param {boolean} [emptyFields=false] - Indicates if the field is empty to display error styling.
 * @param {string} [readonlyBorderColor=colors.shades800] - Border color in readonly mode.
 * @param {string} [readonlyCheckedColor=colors.shades800] - Checked color in readonly mode.
 * @param {string} [readonlyBackgroundColor=colors.shades200] - Background color in readonly mode.
 * @param {string} [readonlyTextColor=colors.general950] - Text color in readonly mode.
 *
 * @returns {JSX.Element} The rendered day selector component.
 *
 * @example
 * <CustomDaySelector
 *   dayArray={[1,3,5]}
 *   setDayArray={setSelectedDays}
 *   readonly={false}
 *   emptyFields={selectedDays.length === 0}
 * />
 */
const CustomDaySelector = ({ id="cb_cds_day_selector", dayArray, setDayArray,
                               containerClasses='', readonly=false,
                               required=false, emptyFields=false,
                               readonlyBorderColor =  colors.shades800, readonlyCheckedColor=colors.shades800,
                               readonlyBackgroundColor = colors.shades200, readonlyTextColor=colors.general950}) => {
    const { t } = useTranslation();

    const handleDay = (event, dayValue) => {
        const value = parseInt(dayValue);
        const index = dayArray.indexOf(value);

        if (event.target.checked && index === -1) {
            setDayArray((prevArray) => [...prevArray, value]);
        } else if (!event.target.checked && index !== -1) {
            setDayArray((prevArray) => prevArray.filter((item) => item !== value));
        }
    };

    return (
        <div className={'main_content days_card '+ containerClasses}   >
            <Box className={`d-flex justify-content-between day-selector-checkbox ${readonly? 'readonly':''}`}>
                {DAYS_MODEL.map(({ value, label }) => {
                    return (
                        <CustomDayCheckbox
                            key={value}
                            value={value}
                            label={t(label)}
                            checked={dayArray?.includes(value)}
                            onChange={(e) => handleDay(e, value)}
                            id={id + "_" + value}
                            required={required}
                            emptyFields={emptyFields && dayArray?.length === 0}
                            readonlyBorderColor={readonlyBorderColor}
                            readonlyCheckedColor={readonlyCheckedColor}
                            readonlyBackgroundColor={readonlyBackgroundColor}
                            readonlyTextColor={readonlyTextColor}
                            readonly={readonly}
                        />
                    );
                })}
            </Box>
          {emptyFields && dayArray?.length === 0 && (
            <FormHelperText error={emptyFields && dayArray?.length === 0} sx={CustomStyleFormHelperText(false)}>
                {t('requiredField')}
            </FormHelperText> )}
        </div>
    );
};

export default CustomDaySelector;
